import { h, empty } from '../../../../../src/shared/components/dom.js';
import { Spinner } from '../../../../../src/shared/components/spinner.js';
import { EmptyState } from '../../../../../src/shared/components/empty-state.js';
import { setPageTitle } from '../../../../../src/shared/page-title.js';
import { LibraryGridTile as GridTile } from '../../components/library-grid-tile.js';
import { navigate } from '../../router.js';
import { openListNameModal } from '../../components/list-name-modal.js';
import {
  getList, updateList, deleteList,
  addListItem, removeListItem, reorderListItems,
  getCatalog,
  toggleListReaction, getListReactions,
} from '../../api.js';
import { state, DEFAULT_FILTERS } from '../../state.js';
import { releaseTypesParam, includedHiddenTypes, hasNarrowingFilters, sanitizeReleaseTypes } from '../../browse-state.js';
import { LIBRARY_SYSTEMS, systemName, enabledLibrarySystems } from '../../shared/system-meta.js';
import { FilterSidebar } from '../index/filter-sidebar.js';
import { createPageRetryGate } from '../page-retry-gate.js';
import { libraryPlayUrl } from '../../play-url.js';

/**
 * List detail screen — /library/list/:id
 *
 * Two modes:
 *
 *   Owners  — full editor with a three-column layout:
 *               filters | catalog | members.
 *             The leftmost column mounts the main library's shared
 *             FilterSidebar (genre chips, Top 100 / favorites /
 *             played / achievements / scores toggles, A–Z, saved
 *             custom filters) with the My-lists and Live-now sections
 *             suppressed. The middle "add games" catalog browser
 *             carries the search / sort / system toolbar. The right
 *             column holds list metadata + edit controls + the
 *             drag-drop members grid. At narrow widths the filters
 *             column collapses into a ☰ Filters drawer.
 *             The built-in queue is the focused exception: queue details +
 *             members occupy the left half, the add-games catalog occupies
 *             the right half, and filters live in that existing drawer.
 *
 *   Viewers — read-only tile grid, used for public lists.
 *
 * Reuses GridTile for both panes' tile rendering, getCatalog for the
 * catalog fetch (merging the toolbar + sidebar filter state, then
 * applying the Top 100 / achievements / scores client-side
 * post-filters exactly as the index does), and the existing api
 * mutation wrappers (addListItem / removeListItem) — which dispatch
 * library:lists-changed so the sidebar count stays in sync
 * automatically.
 */
export async function render(params, { root, isCurrent = () => true }) {
  empty(root);
  root.appendChild(Spinner({ label: 'Loading list…' }));

  // List IDs are UUIDs (migration 0018). Accept any well-formed UUID
  // string; the server does an authoritative check too, we just fail
  // fast on obvious junk.
  const id = typeof params.id === 'string'
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(params.id)
    ? params.id.toLowerCase()
    : null;
  if (!id) {
    empty(root);
    setPageTitle('Invalid list');
    root.appendChild(EmptyState({
      title: 'Not a valid list',
      message: 'This URL doesn\'t look like a list I can open.',
      action: h('a', { class: 'lib-btn', href: '/library' }, '← Back to library'),
      headingLevel: 'h1',
    }));
    return false;
  }

  let list;
  try {
    list = await getList(id);
    if (!isCurrent()) return false;
  } catch (err) {
    if (!isCurrent()) return false;
    empty(root);
    setPageTitle(err?.status === 404 ? 'List not found' : 'List unavailable');
    root.appendChild(EmptyState({
      title: err?.status === 404 ? 'List not found' : 'Couldn\'t load list',
      message: err?.status === 404
        ? 'This list doesn\'t exist, or it\'s private.'
        : 'Try again in a moment.',
      action: h('a', { class: 'lib-btn', href: '/library' }, '← Back to library'),
      headingLevel: 'h1',
    }));
    return false;
  }

  empty(root);
  setPageTitle(`${list.name} - List`);
  if (list.is_owner) {
    root.appendChild(buildEditorScreen(list));
  } else {
    root.appendChild(buildViewerScreen(list));
  }
  return true;
}

// ════════════════════════════════════════════════════════════════════
// OWNER EDITOR — two-pane layout (catalog "add games" on left/narrower,
// list metadata + members on right/wider). Task 6 prepends a filters
// column to the left of the catalog pane.
// ════════════════════════════════════════════════════════════════════

export function buildEditorScreen(list) {
  const screen = h('section', { class: 'lib-list-editor' });

  screen.appendChild(
    h('a', { class: 'lib-game-back', href: '/library' }, '← Library')
  );

  // Track in-list state as a Set<game_id> for O(1) membership checks
  // during catalog render and toggle. Kept in sync with list.items as
  // the user adds/removes.
  const inListSet = new Set(list.items.map(i => Number(i.game_id)));

  // Custom lists keep the three-column editor: filters | catalog | members.
  // The built-in queue is a focused 50/50 editor: queue + members | catalog.
  // Its filters stay available through the same drawer used at narrow widths.
  const isQueue = list.kind === 'queue';
  const gridWrap = h('div', {
    class: `lib-list-editor-grid${isQueue ? ' lib-list-editor-grid-queue' : ''}`,
  });
  const filtersPane = h('div', { class: 'lib-list-editor-filters-pane' });
  const catalogPane = h('div', { class: 'lib-list-editor-catalog-pane' });
  const listPane = h('div', { class: 'lib-list-editor-list-pane' });
  if (isQueue) gridWrap.append(listPane, catalogPane);
  else gridWrap.append(filtersPane, catalogPane, listPane);
  screen.appendChild(gridWrap);

  // Narrow-width filter drawer (reuses the index's lib-filter-drawer CSS).
  const drawerBackdrop = h('div', {
    class: 'lib-filter-drawer-backdrop', inert: true, 'aria-hidden': 'true',
  });
  const drawerBody = h('div', { class: 'lib-filter-drawer-body' });
  let drawerKeyHandler = null;
  function closeDrawer(restoreFocus = true) {
    if (!drawerBackdrop.classList.contains('open')) return;
    drawerBackdrop.classList.remove('open');
    drawerBackdrop.setAttribute('inert', '');
    drawerBackdrop.setAttribute('aria-hidden', 'true');
    for (const child of screen.children) if (child !== drawerBackdrop) child.removeAttribute('inert');
    document.removeEventListener('keydown', drawerKeyHandler);
    drawerKeyHandler = null;
    if (restoreFocus) filterToggleBtn.focus();
  }
  const drawer = h('div', {
    class: 'lib-filter-drawer', role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Filters',
  },
    h('div', { class: 'lib-filter-drawer-header' },
      h('span', { class: 'lib-filter-drawer-title' }, 'Filters'),
      h('button', { class: 'lib-filter-drawer-close', type: 'button', 'aria-label': 'Close',
        onclick: closeDrawer }, '✕'),
    ),
    drawerBody,
  );
  drawerBackdrop.append(drawer);
  drawerBackdrop.addEventListener('click', (e) => {
    if (e.target === drawerBackdrop) closeDrawer();
    if (e.target.closest('a[href]')) closeDrawer(false);
  });
  screen.appendChild(drawerBackdrop);

  // ── LIST PANE (right) ─────────────────────────────────────────────
  // Title + Rename / Delete. The system queue is non-renamable and
  // non-deletable (server enforces with 403 queue_protected) — show a
  // badge where the action buttons would be.
  listPane.appendChild(
    h('div', { class: 'lib-list-header' },
      h('h1', { class: 'lib-list-title' }, list.name),
      list.kind === 'queue'
        ? h('span', {
            class: 'lib-list-queue-badge',
            title: 'Your built-in play queue: always here, can\'t be deleted.',
          }, 'QUEUE')
        : h('div', { class: 'lib-list-actions' },
            h('button', {
              class: 'lib-btn lib-btn-ghost',
              type: 'button',
              onclick: () => promptRename(list),
            }, 'Rename'),
            h('button', {
              class: 'lib-btn lib-btn-ghost',
              type: 'button',
              onclick: () => promptDelete(list),
            }, 'Delete'),
          ),
    )
  );

  // Description (editable) + meta line with visibility toggle
  listPane.appendChild(renderDescription(list));
  listPane.appendChild(renderMetaLine(list));
  listPane.appendChild(renderReactionBar(list));

  // Current members — compact GridTile grid rendered in-place.
  // Re-rendered on add/remove so the two panes stay coherent.
  listPane.appendChild(
    h('h2', { class: 'lib-list-editor-section-heading' }, 'In this list')
  );
  const membersContainer = h('div', { class: 'lib-grid lib-grid-tiles lib-list-members-grid' });
  listPane.appendChild(membersContainer);
  const membersEmptyHint = h('p', { class: 'lib-list-empty-hint lib-list-members-hint' },
    'Search and add games from the catalog.');
  listPane.appendChild(membersEmptyHint);

  function renderMembers() {
    empty(membersContainer);
    // Keep the header count in sync with list.items as the user adds /
    // removes. Queried each call rather than cached on build because
    // the metadata line renders *after* the empty members container
    // is created (ordering inside buildEditorScreen).
    const countEl = document.getElementById('lib-list-count');
    if (countEl) {
      const n = list.items.length;
      countEl.textContent = `${n} ${n === 1 ? 'game' : 'games'}`;
    }
    if (list.items.length === 0) {
      membersContainer.style.display = 'none';
      membersEmptyHint.style.display = '';
      return;
    }
    membersContainer.style.display = '';
    membersEmptyHint.style.display = 'none';
    for (const item of list.items) {
      membersContainer.appendChild(renderMemberTile(item));
    }
  }

  function renderMemberTile(item) {
    // Reuse GridTile by shimming the item shape into a rom-like object
    // (same adapter the viewer path uses). The title links to the game
    // detail page; the × overlay (owner-only) removes it from the list.
    const rom = romFromItem(item);
    const wrap = h('div', {
      class: 'lib-list-tile-wrap',
      draggable: 'true',
      // The game_id stays the stable handle through reorder — indexes
      // shift, ids don't. Read from the dataset at drop time.
      'data-game-id': String(item.game_id),
    });
    wrap.appendChild(GridTile({
      rom,
      onPlay: () => launchGame(rom),
      onDetail: () => openDetail(rom),
    }));
    wrap.appendChild(h('button', {
      class: 'lib-list-remove-btn',
      type: 'button',
      title: `Remove ${rom.displayName} from ${list.name}`,
      'aria-label': `Remove ${rom.displayName}`,
      onclick: async (e) => {
        e.stopPropagation();
        await removeFromListFlow(Number(item.game_id), rom.displayName);
      },
    }, '✕'));

    // Drag-and-drop reorder — see setupDnd below. The wrap element is
    // the draggable; GridTile's internals stay uninvolved.
    attachDnd(wrap, Number(item.game_id));
    return wrap;
  }

  // ── Drag-and-drop reorder (Phase 7.5) ──────────────────────────
  // HTML5 DnD on member tile wrappers. Strategy: track the dragged
  // game_id, compute the insertion index on dragover (before/after
  // target tile depending on cursor-in-half), splice the items array
  // on drop, re-render optimistically, POST /reorder. Revert on
  // server failure.
  let draggingId = null;   // game_id currently being dragged
  let dropBefore = null;   // game_id of the tile the cursor is over
  let _dragActive = false; // true between dragstart and post-dragend frame

  function attachDnd(el, gameId) {
    el.addEventListener('dragstart', (e) => {
      _dragActive = true;
      draggingId = gameId;
      el.classList.add('dragging');
      // Firefox needs any dataTransfer payload or the drag won't fire.
      try { e.dataTransfer.setData('text/plain', String(gameId)); } catch {}
      e.dataTransfer.effectAllowed = 'move';
    });
    el.addEventListener('dragend', () => {
      draggingId = null;
      dropBefore = null;
      // Clear any lingering visual state on all member tiles.
      membersContainer.querySelectorAll('.dragging, .drop-before, .drop-after')
        .forEach(n => n.classList.remove('dragging', 'drop-before', 'drop-after'));
      // Defer clearing _dragActive so any click dispatched in the same
      // frame is still suppressed by the capture guard below.
      requestAnimationFrame(() => { _dragActive = false; });
    });
    // Suppress click events that fire during/after a drag — without this
    // the GridTile's click handler navigates away mid-drag, removing the
    // drag source and leaving the browser in a stuck drag state.
    el.addEventListener('click', (e) => {
      if (_dragActive) {
        e.stopPropagation();
        e.preventDefault();
      }
    }, true);
    el.addEventListener('dragover', (e) => {
      if (draggingId == null || draggingId === gameId) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      // Cursor in the left half of the target → insert before;
      // right half → insert after. Tiles are wide enough that this
      // two-zone granularity is fine; no need for the 3-zone fancy
      // version.
      const rect = el.getBoundingClientRect();
      const before = (e.clientX - rect.left) < rect.width / 2;
      el.classList.toggle('drop-before', before);
      el.classList.toggle('drop-after', !before);
      dropBefore = before ? gameId : nextGameIdAfter(gameId);
    });
    el.addEventListener('dragleave', () => {
      el.classList.remove('drop-before', 'drop-after');
    });
    el.addEventListener('drop', async (e) => {
      e.preventDefault();
      const sourceId = draggingId;
      const targetBefore = dropBefore;
      // Reset visuals immediately — dragend also fires, but belt-and-
      // suspenders (some browsers delay dragend on fast drops).
      membersContainer.querySelectorAll('.drop-before, .drop-after')
        .forEach(n => n.classList.remove('drop-before', 'drop-after'));
      if (sourceId == null || sourceId === gameId) return;
      await moveItem(sourceId, targetBefore);
    });
  }

  /** Resolve the game_id of the item immediately after the given one
   *  in the current list. Returns null when it's the last item. */
  function nextGameIdAfter(gameId) {
    const idx = list.items.findIndex(i => Number(i.game_id) === gameId);
    const nxt = list.items[idx + 1];
    return nxt ? Number(nxt.game_id) : null;
  }

  /** Move `sourceId` so it sits immediately before `beforeId` (null =
   *  "to the end"). Optimistic local update + server POST. */
  async function moveItem(sourceId, beforeId) {
    const prevItems = list.items.slice();
    const fromIdx = list.items.findIndex(i => Number(i.game_id) === sourceId);
    if (fromIdx < 0) return;
    const [moving] = list.items.splice(fromIdx, 1);

    let toIdx;
    if (beforeId == null) {
      toIdx = list.items.length;
    } else {
      toIdx = list.items.findIndex(i => Number(i.game_id) === beforeId);
      if (toIdx < 0) toIdx = list.items.length;
    }
    list.items.splice(toIdx, 0, moving);
    renderMembers();

    try {
      await reorderListItems(list.id, list.items.map(i => Number(i.game_id)));
    } catch (err) {
      // Revert UI on failure.
      list.items = prevItems;
      renderMembers();
      // eslint-disable-next-line no-alert
      alert('Could not save the new order.');
      console.error('[list-editor] reorder failed:', err);
    }
  }

  // Safety net: if a previous drag session was never cleaned up (e.g.
  // tab switch, window blur, browser quirk), reset stale state on the
  // next pointer interaction so the page doesn't stay stuck.
  membersContainer.addEventListener('pointerdown', () => {
    if (_dragActive) {
      _dragActive = false;
      draggingId = null;
      dropBefore = null;
      membersContainer.querySelectorAll('.dragging, .drop-before, .drop-after')
        .forEach(n => n.classList.remove('dragging', 'drop-before', 'drop-after'));
    }
  }, true);

  renderMembers();

  // ── CATALOG PANE (middle) ─────────────────────────────────────────
  // Toolbar carries the high-frequency controls that don't live in the
  // sidebar: a search box, a sort <select>, a system <select>, and a
  // ☰ Filters drawer toggle (visible only when the filters column is
  // collapsed). Everything else — genre, Top 100, favorites,
  // played/unplayed, achievements, scores, A–Z, saved custom filters —
  // is owned by the shared FilterSidebar mounted in filtersPane.
  //
  // Filter state is split in two, mirroring the index:
  //   `toolbar`        — search / sort / system, written by the toolbar
  //                      listeners below.
  //   `sidebarFilters` — the index's catalog.filters shape, owned by
  //                      the FilterSidebar via its onChange callback.
  // loadCatalog() merges both into the getCatalog request and applies
  // the client-side post-filters (top100 / achievements / scores).
  const toolbar = { search: '', sort: 'title', system: '' };
  let sidebarFilters = { ...DEFAULT_FILTERS };

  const searchInput = h('input', {
    type: 'search',
    class: 'lib-search',
    placeholder: 'Search games to add…',
    'aria-label': 'Search library',
  });
  const sortSelect = h('select', { class: 'lib-select', 'aria-label': 'Sort order' },
    h('option', { value: 'title' }, 'A–Z'),
    h('option', { value: 'recent' }, 'Recently played'),
    h('option', { value: 'favorites' }, 'Favorites first'),
    h('option', { value: 'rating' }, 'Highest rated'),
  );
  const systemSelect = h('select', { class: 'lib-select', 'aria-label': 'Filter by system' },
    h('option', { value: '' }, 'All systems'),
    ...enabledLibrarySystems(state.get().features).map(s => h('option', { value: s.id }, s.name)),
  );

  const filterToggleBtn = h('button', {
    type: 'button', class: 'lib-btn lib-btn-ghost lib-filter-drawer-toggle',
    'aria-label': 'Open filters', title: 'Filters',
    onclick: () => {
      renderSidebarInto(drawerBody);
      drawerBackdrop.removeAttribute('inert');
      drawerBackdrop.removeAttribute('aria-hidden');
      drawerBackdrop.classList.add('open');
      for (const child of screen.children) if (child !== drawerBackdrop) child.setAttribute('inert', '');
      drawer.querySelector('.lib-filter-drawer-close').focus();
      drawerKeyHandler = (event) => {
        if (event.key === 'Escape') { closeDrawer(); return; }
        if (event.key !== 'Tab') return;
        const controls = [...drawer.querySelectorAll('a[href], button:not([disabled]), input, select, [tabindex]:not([tabindex="-1"])')];
        if (!controls.length) return;
        if (event.shiftKey && document.activeElement === controls[0]) {
          event.preventDefault(); controls.at(-1).focus();
        } else if (!event.shiftKey && document.activeElement === controls.at(-1)) {
          event.preventDefault(); controls[0].focus();
        }
      };
      document.addEventListener('keydown', drawerKeyHandler);
    },
  }, '☰ Filters');

  const toolbarPrimary = h('div', { class: 'lib-list-editor-toolbar' },
    filterToggleBtn, searchInput, sortSelect, systemSelect);
  const statusLine = h('p', { class: 'lib-results-status', 'aria-live': 'polite' });
  const resultsHeading = h('h2', { class: 'lib-results-heading', tabindex: '-1' }, 'Catalog results');
  const hiddenHint = h('p', { class: 'lib-results-hidden-hint', role: 'status', 'aria-live': 'polite' });
  const catalogGrid = h('div', { class: 'lib-grid lib-grid-tiles lib-list-editor-catalog' });
  catalogPane.append(toolbarPrimary, resultsHeading, statusLine, hiddenHint, catalogGrid);

  // ── FILTERS PANE (left) ───────────────────────────────────────────
  // Mounts the shared FilterSidebar (stateless — re-rendered on each
  // change), targeting either the inline column or the drawer body.
  // Live-now is suppressed (a play-status card isn't useful mid-edit);
  // My-lists stays so you can jump between your lists from here.
  function renderSidebarInto(target) {
    empty(target);
    target.appendChild(FilterSidebar({
      filters: sidebarFilters,
      system: toolbar.system || 'all',
      skipLiveNow: true,
      onChange: (next, focusType) => {
        sidebarFilters = next;
        renderSidebarInto(filtersPane);
        if (drawerBackdrop.classList.contains('open')) renderSidebarInto(drawerBody);
        loadCatalog();
        if (focusType) target.querySelector(`[data-release-control="${focusType}"]`)?.focus();
      },
    }));
  }
  renderSidebarInto(filtersPane);

  // Catalog loader — same pattern as index-screen.js (debounce +
  // AbortController + IntersectionObserver infinite scroll).
  let allItems = [];
  const PAGE_SIZE = 100;     // tiles rendered per batch as the sentinel scrolls in
  let renderedCount = 0;     // tiles currently in the DOM
  let catalogTotal = 0;
  let nextOffset = 0;
  let catalogHasMore = false;
  let catObserver = null;    // IntersectionObserver watching the sentinel
  let catSentinel = null;    // sentinel element at the bottom of the catalog
  let loadDebounce = null;
  let currentAbort = null;
  let loadMoreAbort = null;
  let catalogToken = 0;
  const pageRetry = createPageRetryGate();

  function catalogQuery(offset) {
    return {
      search: toolbar.search || undefined,
      sort: toolbar.sort,
      system: toolbar.system && toolbar.system !== 'all' ? toolbar.system : undefined,
      genre: sidebarFilters.genre || undefined,
      tag: sidebarFilters.tag || undefined,
      favorite: sidebarFilters.favorite ? 'true' : undefined,
      played: sidebarFilters.played || undefined,
      letter: sidebarFilters.letter || undefined,
      filter_id: sidebarFilters.filter_id || undefined,
      top100: sidebarFilters.top100 ? 'true' : undefined,
      achievements: sidebarFilters.achievements ? 'true' : undefined,
      scores: sidebarFilters.scores ? 'true' : undefined,
      release_types: releaseTypesParam(sidebarFilters, state.get().features),
      limit: PAGE_SIZE,
      offset,
    };
  }

  async function loadCatalog() {
    const token = ++catalogToken;
    if (currentAbort) currentAbort.abort();
    if (loadMoreAbort) loadMoreAbort.abort();
    loadMoreAbort = null;
    currentAbort = new AbortController();
    const signal = currentAbort.signal;
    empty(catalogGrid);
    destroyCatObserver();
    catalogGrid.appendChild(Spinner({ label: 'Loading games…' }));
    statusLine.textContent = '';
    allItems = [];
    renderedCount = 0;
    catalogTotal = 0;
    nextOffset = 0;
    catalogHasMore = false;
    pageRetry.reset();
    hiddenHint.replaceChildren();
    hiddenHint.classList.remove('lib-visually-hidden');
    if (releaseTypesParam(sidebarFilters, state.get().features) === null) {
      hiddenHint.classList.add('lib-visually-hidden');
      hiddenHint.textContent = 'Choose a release type to see games.';
      empty(catalogGrid);
      catalogGrid.append(
        h('p', {}, 'Choose a release type to see games.'),
        h('button', { type: 'button', class: 'lib-btn', onclick: () => {
          sidebarFilters = { ...sidebarFilters, release_types: ['official'] };
          renderSidebarInto(filtersPane);
          if (drawerBackdrop.classList.contains('open')) renderSidebarInto(drawerBody);
          loadCatalog();
          resultsHeading.focus();
        } }, 'Show official releases'),
      );
      return;
    }
    try {
      const data = await getCatalog(catalogQuery(0), { signal });
      if (token !== catalogToken) return;
      allItems = data.items || [];
      catalogTotal = Number(data.total) || 0;
      nextOffset = data.next_offset == null ? allItems.length : Number(data.next_offset);
      catalogHasMore = data.has_more === true;
      renderHiddenHint(data.hidden_by_release_type);
      renderCatalog();
    } catch (err) {
      if (err?.name === 'AbortError') return;
      if (token !== catalogToken) return;
      hiddenHint.replaceChildren();
      empty(catalogGrid);
      const message = err?.body?.error === 'catalog_user_enrichment_unavailable'
        ? 'Your favorites or play history are temporarily unavailable. Retry to load authoritative data.'
        : 'Couldn\'t load the catalog.';
      catalogGrid.appendChild(h('p', { class: 'lib-tile-meta-faded', role: 'status' }, message));
      catalogGrid.appendChild(h('button', { type: 'button', class: 'lib-btn', onclick: loadCatalog }, 'Retry'));
    }
  }

  async function loadMoreCatalog({ explicitRetry = false } = {}) {
    if (!catalogHasMore || releaseTypesParam(sidebarFilters, state.get().features) === null) return;
    const lease = pageRetry.start({ explicit: explicitRetry });
    if (!lease) return;
    const token = catalogToken;
    const controller = new AbortController();
    loadMoreAbort = controller;
    if (explicitRetry) statusLine.textContent = 'Retrying…';
    try {
      const data = await getCatalog(catalogQuery(nextOffset), { signal: controller.signal });
      if (token !== catalogToken || !pageRetry.owns(lease)) return;
      allItems.push(...(data.items || []));
      catalogTotal = Number(data.total) || catalogTotal;
      nextOffset = data.next_offset == null ? allItems.length : Number(data.next_offset);
      catalogHasMore = data.has_more === true;
      pageRetry.succeed(lease);
      appendNextCatalogPage();
      updateCatalogStatus();
    } catch (err) {
      if (err?.name === 'AbortError' || token !== catalogToken || !pageRetry.owns(lease)) return;
      hiddenHint.replaceChildren();
      pageRetry.fail(lease);
      statusLine.replaceChildren(
        document.createTextNode('Couldn\'t load more games. '),
        h('button', {
          type: 'button', class: 'lib-btn',
          onclick: () => loadMoreCatalog({ explicitRetry: true }),
        }, 'Retry'),
      );
    } finally {
      if (loadMoreAbort === controller) loadMoreAbort = null;
      const ownedCurrentGeneration = pageRetry.finish(lease);
      if (ownedCurrentGeneration && token === catalogToken && pageRetry.shouldRearm(catalogHasMore)) {
        setupCatObserver();
      }
    }
  }

  function updateCatalogStatus() {
    statusLine.textContent = `${catalogTotal.toLocaleString()} game${catalogTotal === 1 ? '' : 's'}`;
  }

  function renderCatalog() {
    empty(catalogGrid);
    destroyCatObserver();
    renderedCount = 0;
    const total = catalogTotal;
    if (total === 0) {
      statusLine.textContent = hiddenHint.textContent ? '' : 'No games match.';
      return;
    }
    updateCatalogStatus();
    // Render the first batch, then arm infinite scroll if more remain.
    appendNextCatalogPage();
    if (pageRetry.shouldRearm(catalogHasMore)) setupCatObserver();
  }

  function renderHiddenHint(hidden) {
    hiddenHint.replaceChildren();
    hiddenHint.classList.remove('lib-visually-hidden');
    if (state.get().features?.library_release_types !== true) return;
    const types = includedHiddenTypes(sidebarFilters.release_types, hidden);
    if (!types.length || (allItems.length > 0 && !hasNarrowingFilters(toolbar.search, sidebarFilters))) return;
    const count = Number(hidden.total);
    hiddenHint.append(
      document.createTextNode(`${count} more match${count === 1 ? '' : 'es'} ${count === 1 ? 'is' : 'are'} hidden by release type. `),
      h('button', { type: 'button', class: 'lib-btn', onclick: () => {
        sidebarFilters = {
          ...sidebarFilters,
          release_types: [...sanitizeReleaseTypes(sidebarFilters.release_types), ...types],
        };
        renderSidebarInto(filtersPane);
        if (drawerBackdrop.classList.contains('open')) renderSidebarInto(drawerBody);
        loadCatalog();
        resultsHeading.focus();
      } }, 'Include them'),
    );
  }

  function appendNextCatalogPage() {
    const end = Math.min(renderedCount + PAGE_SIZE, allItems.length);
    for (let i = renderedCount; i < end; i++) {
      catalogGrid.appendChild(renderCatalogTile(allItems[i]));
    }
    renderedCount = end;
    if (renderedCount >= allItems.length && !catalogHasMore) destroyCatObserver();
  }

  // IntersectionObserver infinite scroll, mirroring index-screen.js. The
  // sentinel sits just below the grid in the (page-scrolling) catalog
  // pane; when it nears the viewport we append the next batch and rebuild
  // it so the observer re-fires.
  function setupCatObserver() {
    destroyCatObserver();
    catSentinel = h('div', { class: 'lib-scroll-sentinel', 'aria-hidden': 'true' },
      Spinner({ label: 'Loading more…' }));
    catalogPane.appendChild(catSentinel);
    catObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && !pageRetry.loading && catalogHasMore) {
          destroyCatObserver();
          loadMoreCatalog();
        }
      }
    }, { rootMargin: '400px' });
    catObserver.observe(catSentinel);
  }

  function destroyCatObserver() {
    if (catObserver) { catObserver.disconnect(); catObserver = null; }
    if (catSentinel) { catSentinel.remove(); catSentinel = null; }
  }

  // Flip a single catalog tile's add/remove state in place — avoids a full
  // re-render (which would reset the infinite-scroll position) when a game
  // is added to / removed from the list.
  function applyCatalogToggleState(toggle, inList) {
    const title = toggle.dataset.gameTitle || 'game';
    toggle.classList.toggle('active', inList);
    toggle.textContent = inList ? '− Remove' : '+ Add to list';
    toggle.title = inList ? 'Remove from list' : 'Add to list';
    toggle.setAttribute(
      'aria-label',
      inList ? `Remove ${title} from ${list.name}` : `Add ${title} to ${list.name}`,
    );
    toggle.setAttribute('aria-pressed', inList ? 'true' : 'false');
  }

  function syncCatalogTile(gameId) {
    const tile = catalogGrid.querySelector(`[data-game-id="${gameId}"]`);
    if (!tile) return; // not currently rendered (scrolled past, or filtered out)
    const inList = inListSet.has(Number(gameId));
    tile.classList.toggle('in-list', inList);
    const toggle = tile.querySelector('.lib-editor-cat-toggle');
    if (toggle) {
      applyCatalogToggleState(toggle, inList);
    }
  }

  function renderCatalogTile(rom) {
    // The game title remains a normal detail link. List membership is a
    // separate, explicitly named toggle below the non-interactive card.
    const gameId = rom.game_id != null ? Number(rom.game_id) : null;
    const inList = gameId != null && inListSet.has(gameId);
    const wrap = h('div', {
      class: `lib-editor-cat-tile${inList ? ' in-list' : ''}`,
      ...(gameId != null ? { 'data-game-id': String(gameId) } : {}),
    });

    wrap.appendChild(GridTile({
      rom,
      onDetail: () => openDetail(rom),
      showActions: false,
    }));

    if (gameId != null) {
      const toggle = h('button', {
        class: `lib-editor-cat-toggle${inList ? ' active' : ''}`,
        type: 'button',
        'data-game-title': rom.displayName || rom.title || 'game',
        onclick: (e) => {
          e.stopPropagation();
          toggleMembership(rom);
        },
      });
      applyCatalogToggleState(toggle, inList);
      wrap.appendChild(toggle);
    }

    return wrap;
  }

  async function toggleMembership(rom) {
    const gameId = rom.game_id != null ? Number(rom.game_id) : null;
    if (!gameId) return;
    if (inListSet.has(gameId)) {
      await removeFromListFlow(gameId, rom.displayName || rom.title || 'this game');
    } else {
      await addToListFlow(rom);
    }
  }

  async function addToListFlow(rom) {
    const gameId = Number(rom.game_id);
    // Optimistic: mark as in-list immediately so the UI feels instant.
    inListSet.add(gameId);
    list.items.push({
      game_id: gameId,
      position: list.items.length + 1, // placeholder; server will confirm
      note: null,
      added_at: new Date().toISOString(),
      game: {
        slug: rom.game_slug || null,
        title: rom.displayName || rom.title || '(untitled)',
        system_id: rom.system || rom.game_system || null,
        release_type: rom.release_type,
        release_detail: rom.release_detail,
        cover_url: null, // filled by detail page on its next fetch
        release_year: rom.release_year || null,
        genres: rom.genres || [],
      },
    });
    renderMembers();
    syncCatalogTile(gameId);

    try {
      await addListItem(list.id, gameId);
    } catch (err) {
      // Revert on failure.
      inListSet.delete(gameId);
      list.items = list.items.filter(i => Number(i.game_id) !== gameId);
      renderMembers();
      syncCatalogTile(gameId);
      // eslint-disable-next-line no-alert
      alert('Could not add to list.');
      console.error('[list-editor] add failed:', err);
    }
  }

  async function removeFromListFlow(gameId, title) {
    // Optimistic remove.
    const prevItems = list.items.slice();
    inListSet.delete(gameId);
    list.items = list.items.filter(i => Number(i.game_id) !== gameId);
    renderMembers();
    syncCatalogTile(gameId);

    try {
      await removeListItem(list.id, gameId);
    } catch (err) {
      inListSet.add(gameId);
      list.items = prevItems;
      renderMembers();
      syncCatalogTile(gameId);
      // eslint-disable-next-line no-alert
      alert(`Could not remove "${title}".`);
      console.error('[list-editor] remove failed:', err);
    }
  }

  // Wire up the toolbar controls. Search is debounced (200ms) so typing
  // doesn't fire a request per keystroke; sort/system fire immediately
  // since each is a single user action. AbortController inside
  // loadCatalog cancels stale fetches. The sidebar's own controls feed
  // back through renderSidebarInto's onChange (see above).
  searchInput.addEventListener('input', () => {
    toolbar.search = searchInput.value.trim();
    clearTimeout(loadDebounce);
    loadDebounce = setTimeout(loadCatalog, 200);
  });
  sortSelect.addEventListener('change', () => { toolbar.sort = sortSelect.value; loadCatalog(); });
  systemSelect.addEventListener('change', () => {
    toolbar.system = systemSelect.value;
    renderSidebarInto(filtersPane);  // genre chips re-scope to the new system
    loadCatalog();
  });

  loadCatalog();
  return screen;
}

// ════════════════════════════════════════════════════════════════════
// VIEWER (read-only) — kept for public lists / non-owner views.
// ════════════════════════════════════════════════════════════════════

function buildViewerScreen(list) {
  const screen = h('section', { class: 'lib-list-screen' });

  screen.appendChild(
    h('a', { class: 'lib-game-back', href: '/library' }, '← Library')
  );

  screen.appendChild(
    h('div', { class: 'lib-list-header' },
      h('h1', { class: 'lib-list-title' }, list.name),
    )
  );

  screen.appendChild(renderDescription(list));
  screen.appendChild(renderMetaLine(list));
  screen.appendChild(renderReactionBar(list));

  if (!list.items || list.items.length === 0) {
    screen.appendChild(renderEmpty(list));
    return screen;
  }

  const grid = h('div', { class: 'lib-grid lib-grid-tiles' });
  for (const item of list.items) {
    const rom = romFromItem(item);
    grid.appendChild(GridTile({
      rom,
      onPlay: () => launchGame(rom),
      onDetail: () => openDetail(rom),
    }));
  }
  screen.appendChild(grid);
  return screen;
}

// ════════════════════════════════════════════════════════════════════
// Shared helpers (used by both editor + viewer)
// ════════════════════════════════════════════════════════════════════

/**
 * Turn a server list-item into the `rom` shape GridTile expects.
 * The server's /api/library/lists/:id response now includes
 * `rom_hash` + `rom_system` for a representative ROM (the verified
 * `[!]` dump when available, otherwise the lowest-id row for the
 * game) — passing those through unlocks the shared GridTile's
 * favorite-toggle button for list view tiles.
 */
function romFromItem(item) {
  return {
    name: item.game?.slug || '',
    displayName: item.game?.title || '(untitled)',
    system: item.rom_system || item.game?.system_id || null,
    hash: item.rom_hash || null,
    rom_filename: item.rom_filename || null,
    game_id: item.game_id,
    game_slug: item.game?.slug || null,
    release_year: item.game?.release_year || null,
    genres: item.game?.genres || [],
    release_type: item.release_type ?? item.game?.release_type,
    release_detail: item.release_detail ?? item.game?.release_detail,
    size: 0,
  };
}

function launchGame(rom) {
  if (rom.rom_filename && rom.system) {
    location.assign(libraryPlayUrl(rom.system, rom.rom_filename));
    return;
  }
  openDetail(rom);
}

function openDetail(rom) {
  const slug = rom.game_slug || rom.game_id;
  if (slug && rom.system) {
    navigate(`/library/g/${rom.system}/${slug}`);
  }
}

async function promptRename(list) {
  let error = '';
  let value = list.name;
  for (;;) {
    const next = await openListNameModal({ title: 'Rename list', value, error });
    if (!next || next === list.name) return;
    value = next;
    try {
      await updateList(list.id, { name: next });
      navigate(`/library/list/${list.id}`, { replace: true });
      return;
    } catch (err) {
      error = err?.body?.error === 'name_taken'
        ? 'You already have a list with that name.'
        : 'Could not rename the list.';
    }
  }
}

async function promptDelete(list) {
  // eslint-disable-next-line no-alert
  if (!confirm(`Delete "${list.name}"? This removes the list and all its items. This can't be undone.`)) return;
  try {
    await deleteList(list.id);
    navigate('/library');
  } catch (err) {
    // eslint-disable-next-line no-alert
    alert('Could not delete the list.');
    console.error('[list] delete failed:', err);
  }
}

// ── Description (click-to-edit for owners, static paragraph for viewers) ──
function renderDescription(list) {
  if (!list.is_owner && !list.description) return h('span', { class: 'lib-hidden' });

  const initial = list.description || '';
  const placeholder = '+ Add a description';

  const p = h('p', {
    class: `lib-list-description${list.is_owner ? ' editable' : ''}${!initial ? ' empty' : ''}`,
    ...(list.is_owner ? { contenteditable: 'plaintext-only', 'data-placeholder': placeholder } : {}),
  }, initial);

  if (!list.is_owner) return p;

  let original = initial;
  let committing = false;

  p.addEventListener('focus', () => {
    if (!p.textContent.trim() && p.classList.contains('empty')) {
      p.classList.remove('empty');
    }
  });

  p.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); p.blur(); }
    else if (e.key === 'Escape') {
      e.preventDefault();
      p.textContent = original;
      p.blur();
    }
  });

  p.addEventListener('blur', async () => {
    const next = p.textContent.trim();
    if (next === original || committing) {
      if (!next) p.classList.add('empty');
      return;
    }
    if (next.length > 2000) {
      // eslint-disable-next-line no-alert
      alert('Description is too long (max 2000 chars).');
      p.textContent = original;
      return;
    }
    committing = true;
    try {
      await updateList(list.id, { description: next || null });
      original = next;
      list.description = next;
      if (!next) p.classList.add('empty');
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert('Could not save the description.');
      console.error('[list] description save failed:', err);
      p.textContent = original;
    } finally {
      committing = false;
    }
  });

  return p;
}

// ── Meta line: count + visibility (toggleable for owners) ──────────────
function renderMetaLine(list) {
  const itemCount = list.items?.length || 0;
  const countLabel = `${itemCount} ${itemCount === 1 ? 'game' : 'games'}`;

  if (!list.is_owner) {
    const bits = [countLabel, list.visibility === 'public' ? 'PUBLIC' : 'PRIVATE'];
    return h('p', { class: 'lib-list-meta' }, bits.join(' · '));
  }

  // The queue is locked private — static label instead of the toggle.
  // Keeps the live count span so renderMembers can update it.
  if (list.kind === 'queue') {
    const countSpan = h('span', { class: 'lib-list-count', id: 'lib-list-count' }, countLabel);
    return h('p', { class: 'lib-list-meta' }, countSpan, ' · PRIVATE · QUEUE');
  }

  const visBtn = h('button', {
    type: 'button',
    class: `lib-list-visibility-btn${list.visibility === 'public' ? ' public' : ' private'}`,
    title: list.visibility === 'public'
      ? 'This list is visible to anyone with the link. Click to make private.'
      : 'This list is private. Click to make it visible to anyone with the link.',
    onclick: async () => {
      const next = list.visibility === 'public' ? 'private' : 'public';
      visBtn.disabled = true;
      try {
        await updateList(list.id, { visibility: next });
        list.visibility = next;
        visBtn.textContent = next === 'public' ? 'PUBLIC' : 'PRIVATE';
        visBtn.classList.toggle('public', next === 'public');
        visBtn.classList.toggle('private', next === 'private');
        visBtn.title = next === 'public'
          ? 'This list is visible to anyone with the link. Click to make private.'
          : 'This list is private. Click to make it visible to anyone with the link.';
      } catch (err) {
        // eslint-disable-next-line no-alert
        alert('Could not change visibility.');
        console.error('[list] visibility toggle failed:', err);
      } finally {
        visBtn.disabled = false;
      }
    },
  }, list.visibility === 'public' ? 'PUBLIC' : 'PRIVATE');

  // Live count span so renderMembers can update it without re-rendering
  // the whole meta line.
  const countSpan = h('span', { class: 'lib-list-count', id: 'lib-list-count' }, countLabel);
  return h('p', { class: 'lib-list-meta' }, countSpan, ' · ', visBtn);
}

// ── Reaction bar (public lists only) ────────────────────────────────
const EMOJI_MAP = [
  ['fire', '\u{1F525}'],
  ['heart', '❤️'],
  ['clap', '\u{1F44F}'],
  ['laugh', '\u{1F602}'],
  ['skull', '\u{1F480}'],
  ['gamepad', '\u{1F3AE}'],
];

function renderReactionBar(list) {
  if (list.visibility !== 'public') return h('span', { class: 'lib-hidden' });

  const bar = h('div', { class: 'lib-reaction-bar' });
  const buttons = new Map();

  for (const [key, symbol] of EMOJI_MAP) {
    const countSpan = h('span', { class: 'lib-reaction-count' }, '0');
    const btn = h('button', {
      class: 'lib-reaction-btn',
      type: 'button',
      'data-emoji': key,
      onclick: () => handleToggle(key, btn, countSpan),
    }, symbol, ' ', countSpan);
    buttons.set(key, { btn, countSpan });
    bar.appendChild(btn);
  }

  getListReactions(list.id).then(({ reactions }) => {
    const data = reactions[String(list.id)] || [];
    for (const r of data) {
      const entry = buttons.get(r.emoji);
      if (!entry) continue;
      entry.countSpan.textContent = String(r.count);
      entry.btn.classList.toggle('mine', !!r.mine);
    }
  }).catch(() => { /* non-fatal */ });

  async function handleToggle(emoji, btn, countSpan) {
    if (!state.get().user) {
      window.location.assign('/login');
      return;
    }
    const wasMine = btn.classList.contains('mine');
    const cur = parseInt(countSpan.textContent, 10) || 0;
    countSpan.textContent = wasMine ? Math.max(0, cur - 1) : cur + 1;
    btn.classList.toggle('mine', !wasMine);
    try {
      const { action } = await toggleListReaction(list.id, emoji);
      btn.classList.toggle('mine', action === 'added');
    } catch {
      countSpan.textContent = String(cur);
      btn.classList.toggle('mine', wasMine);
    }
  }

  return bar;
}

// ── Empty state (viewer-only; editor shows a hint in the left pane) ──
function renderEmpty(list) {
  return h('div', { class: 'lib-list-empty' },
    h('p', null, 'This list is empty.'),
  );
}
